import type { SupplyExitMode } from '../terms/types'
import {
  getLstAcceptedInputs,
  findSavingsWithdrawEntry,
  savingsSupportsVerb,
  savingsVerbRequires,
} from '@1delta/calldata-sdk'
import { vaultTraits } from '../terms/vault'
import { isSecondaryMarketOnly } from '../vaults/savings/registry'
import type { EarnActionInput, EarnCapability, EarnMarket } from './types'
import { num } from './normalize'

/**
 * What can be done to a row, and what each op needs.
 *
 * **This module exists to move tribal knowledge server-side.** Today the
 * frontend hand-rolls exactly this matrix — `vaultFamily`, `withdrawalStyle`,
 * `isAsyncVaultWithdraw`, `withdrawFamily`, `savingsWithdrawalMode` — because
 * the API tells it the provider and leaves it to work out that sUSDS exits
 * instantly while sUSDe needs a 7-day cooldown and a claim. Every integrator
 * has to rediscover that. Publishing `capabilities[]` means the client renders
 * the CTA from data and posts one shape.
 *
 * See EARN_ENDPOINT_PLAN.md §5.1.
 */

/**
 * Exit modes that are two-step (request → wait → claim) rather than a single
 * synchronous redeem. Mirrors the frontend's `ASYNC_SAVINGS_MODES` plus the
 * async-by-construction families.
 */
const ASYNC_EXIT_MODES: ReadonlySet<SupplyExitMode> = new Set([
  'fixed-cooldown',
  'request-based',
  'queued',
  'fee-or-queued',
  'instant-or-queued',
])

/** Providers whose exit is a request/claim cycle regardless of the row's mode. */
const ASYNC_PROVIDERS: ReadonlySet<string> = new Set([
  'lagoon', // ERC-7540 async redeem
  'gmx', // keeper-executed tickets
])

/**
 * Stamp `capabilities[]` onto a normalized row.
 *
 * Mutates and returns the row — it is called once per row inside the
 * normalizer's own loop, and cloning several thousand rows to avoid a local
 * mutation is a real cost for no benefit.
 */
export function stampCapabilities(row: EarnMarket): EarnMarket {
  row.capabilities =
    row.venueKind === 'lending'
      ? lendingCapabilities(row)
      : vaultCapabilities(row)
  return row
}

/**
 * Lending markets: symmetric, synchronous, and — uniquely today — able to
 * accept an asset the user actually holds.
 *
 * `acceptsPayAsset: true` is not aspirational: `/v1/actions/lending/deposit`
 * already routes `payAsset` through the conversion solver and an aggregator
 * quote. The vault half cannot, which is precisely the gap phase 3 closes, and
 * publishing the difference is what lets a client render the zap input only
 * where it works instead of discovering the 400 at submit time.
 */
function lendingCapabilities(row: EarnMarket): EarnCapability[] {
  const caps: EarnCapability[] = []
  if (row.availability.canDeposit) {
    caps.push({ action: 'deposit', acceptsPayAsset: true })
  }
  if (row.availability.canWithdraw) {
    caps.push({ action: 'withdraw', acceptsReceiveAsset: true })
  }
  return caps
}

/**
 * Providers with no protocol deposit/withdraw at all — the position is bought
 * and sold as an instrument. See {@link EarnCapability.via}.
 */
const SWAP_ROUTED_PROVIDERS: ReadonlySet<string> = new Set([
  'pendle',
  'spectra',
])

/**
 * Providers the phase-3 vault zap does NOT serve: their deposit is not a
 * plain synchronous ERC-4626 call, and each already has its own pay-asset
 * vocabulary or entry shape — LSTs publish `inputs[]` (multi-leg mints),
 * GMX deposits are keeper tickets, HyperCore is a CoreWriter route, and
 * Lagoon entries settle at an epoch. Pendle/Spectra never reach this gate
 * (swap-routed earlier).
 *
 * MUST match `ZAP_EXCLUDED_PROVIDERS` in worker-api's `earn-vault-zap.ts` —
 * this flag is the advertisement, that gate is the route; drift between
 * them is either a picker that 400s or a served path nobody renders.
 */
const ZAP_EXCLUDED_PROVIDERS: ReadonlySet<string> = new Set([
  'lst',
  'gmx',
  'hypercore',
  'lagoon',
])

/**
 * Savings-registry kinds whose DEPOSIT surface is not ERC-4626 (Frankencoin
 * `save`, Yield Basis LT, Wren `mint`, Hyperbeat depositor, wNLP pool-coin
 * entry, Bitway `stake`). Mirror of `NON_4626_DEPOSIT_KINDS` in
 * `earn-vault-zap.ts`.
 */
const NON_4626_DEPOSIT_KINDS: ReadonlySet<string> = new Set([
  'frankencoin',
  'yieldbasis',
  'wren',
  'hyperbeat',
  'native-wnlp',
  'bitway',
])

/**
 * Can `/v1/actions/earn/deposit` convert an arbitrary pay asset into this
 * row's deposit? True exactly where the route's own gate would serve it:
 * a deposit the composer can express as swap → ERC-4626 `deposit(assets,
 * receiver)`. Under-advertising degrades to a missing picker; OVER-
 * advertising builds a zap input that 400s at submit — prefer the former
 * wherever a row's shape is unknowable here.
 */
function acceptsVaultZap(row: EarnMarket, provider: string): boolean {
  if (ZAP_EXCLUDED_PROVIDERS.has(provider)) return false
  const share = row.shareToken?.address ?? row.ref
  if (!share) return false
  // Upshift's split (`evm-2`) generation: the row's address is a bare receipt
  // token and the entry is `deposit(asset, amount, receiver)` on a different
  // contract — a shape the composer's 4626 section cannot express, so the zap
  // route refuses it. Its OLDER vaults are plain 4626 and keep the zap. The
  // split is visible in the row itself (a deposit contract that is not the
  // share token), which is why it is read here rather than hardcoded.
  if (provider === 'upshift' && isSplitUpshiftRow(row, share)) return false
  try {
    const entry = findSavingsWithdrawEntry(row.chainId, share as `0x${string}`)
    if (entry && NON_4626_DEPOSIT_KINDS.has(entry.kind)) return false
  } catch {
    // Registry lookup failing is "unknown shape" — do not advertise.
    return false
  }
  return true
}

/**
 * Does this Upshift row belong to the split (`evm-2`) generation — deposit
 * contract ≠ receipt token?
 *
 * Those vaults are served by Upshift's own builders (`kind: 'upshift'`), take
 * their deposit asset directly and exit through a request/claim pair; the
 * older generations are ordinary ERC-4626 share tokens. Nothing else in the
 * row distinguishes them, and the version string is not published.
 */
function isSplitUpshiftRow(row: EarnMarket, share: string): boolean {
  const deposit = (row.providerMeta ?? {}).depositContract
  return (
    typeof deposit === 'string' &&
    deposit.toLowerCase() !== String(share).toLowerCase()
  )
}

/**
 * Rows whose venue has a mint/redeem leg that OUR callers can never use — the
 * protocol gates it behind KYC or an allowlist — while the share token itself
 * trades freely with real depth. Re Protocol's reUSD/reUSDe are the case: the
 * ICL's `deposit` is `onlyKYCUser` and redemption is role-gated, so for us the
 * position is bought and sold like a PT even though, for a KYC'd institution,
 * it is an ordinary mint-and-redeem vault.
 *
 * This is a ROW-level fact, not a provider-level one — `savings` holds 200+
 * plain instant-4626 rows alongside these two — so it is read from the savings
 * registry, the same source worker-api's routes read. Two derivations of one
 * fact is how a picker and a route drift into disagreeing.
 *
 * NB this is where a swap-routed row can be NAV-priced rather than book-priced
 * (the invariant {@link swapRoutedProvidersArePriceConsistent} pins for whole
 * providers). That is not a contradiction, it is the product: the row reports
 * NAV, the trade fills at market, and the gap between them is exactly what
 * `requires: ['slippage']` exists to bound.
 */
function isSecondaryMarketRow(row: EarnMarket): boolean {
  const meta = row.providerMeta ?? {}
  if (meta.secondaryMarketOnly === true) return true
  const share = row.shareToken?.address ?? row.ref
  if (!share || !row.chainId) return false
  try {
    return isSecondaryMarketOnly(String(row.chainId), share)
  } catch {
    return false
  }
}

/**
 * A Pendle PT is acquired by TRADING, in both directions. There is no
 * "deposit the underlying" leg to fall back on, so:
 *
 *  - both legs are `via: 'swap'` and REQUIRE a slippage tolerance;
 *  - `acceptsPayAsset` / `acceptsReceiveAsset` are `true` and not a
 *    nice-to-have — the swap is the only route, so paying an arbitrary asset
 *    costs nothing extra;
 *  - the exit is synchronous but priced by pool depth (`liquidity.usd`), so a
 *    size that exceeds it does not fail, it just fills badly. That is a
 *    quoting concern, not a capability one.
 *
 * Both route through the already-integrated Pendle aggregator, which handles
 * the pre-maturity AMM sale and the post-maturity par redemption behind one
 * interface (`isPendlePositionMatured`).
 */
function swapRoutedCapabilities(
  row: EarnMarket,
  opts?: { ignoreMintPermission?: boolean },
): EarnCapability[] {
  const caps: EarnCapability[] = []
  // A permission on the PROTOCOL's mint does not close the market — that is
  // the entire point of a secondary-market row, and suppressing the entry here
  // would leave it advertising no way in while $100k trades at par.
  //
  // `gating` reports only the FIRST reason it found, and the mint permission is
  // checked before the pause — so a row that is BOTH gated and paused reads
  // `allowlist-contract`, and testing that alone would re-open an entry on a
  // row we are deliberately not routing. Read the pause directly. A maturity
  // needs no such care: it is resolved ahead of the permission, so a matured
  // instrument still reports `matured` and keeps the leg shut.
  const entryOpen =
    row.availability.canDeposit ||
    (opts?.ignoreMintPermission === true &&
      row.availability.gating === 'allowlist-contract' &&
      (row.providerMeta ?? {}).paused !== true)
  if (entryOpen) {
    caps.push({
      action: 'deposit',
      via: 'swap',
      requires: ['slippage'],
      acceptsPayAsset: true,
    })
  }
  if (row.availability.canWithdraw) {
    caps.push({
      action: 'withdraw',
      via: 'swap',
      requires: ['slippage'],
      acceptsReceiveAsset: true,
    })
  }
  return caps
}

function vaultCapabilities(row: EarnMarket): EarnCapability[] {
  const provider = row.venue.replace(/^vault\./, '')
  const meta = row.providerMeta ?? {}
  const caps: EarnCapability[] = []

  if (SWAP_ROUTED_PROVIDERS.has(provider)) return swapRoutedCapabilities(row)
  if (isSecondaryMarketRow(row))
    return swapRoutedCapabilities(row, { ignoreMintPermission: true })

  if (row.availability.canDeposit) {
    const inputs = depositInputs(row, provider)
    caps.push({
      action: 'deposit',
      inputs,
      requires: depositRequires(provider, meta, inputs),
      // Phase 3 (EARN_ENDPOINT_PLAN §5.3): `/v1/actions/earn/deposit` routes
      // a mismatched `payAsset` through an aggregator swap + composed 4626
      // deposit — but ONLY for rows whose deposit is a plain synchronous
      // ERC-4626 call. Advertising it anywhere else builds a zap input that
      // 400s at submit, which is exactly what this flag existed to prevent.
      acceptsPayAsset: acceptsVaultZap(row, provider),
    })
  }

  if (!row.availability.canWithdraw) return caps

  const mode = row.exit.mode
  // HyperCore's `queued` mode describes a LOCK-UP, not a tx shape: the
  // withdrawal is one CoreWriter route, merely refused until the lock-up
  // elapses. There is no request/claim pair to publish, so it takes the
  // synchronous branch regardless of its exit mode.
  const isAsync =
    provider !== 'hypercore' &&
    (ASYNC_PROVIDERS.has(provider) || ASYNC_EXIT_MODES.has(mode))

  if (!isAsync) {
    caps.push({ action: 'withdraw', requires: withdrawRequires(provider) })
    return caps
  }

  // `fee-or-queued` is genuinely BOTH: an immediate exit that pays a fee, and
  // a free queued one. Publishing only the queue would hide the fast path;
  // publishing only the instant leg would hide that it costs money. Both, with
  // the fee attached to the one that charges it.
  if (mode === 'fee-or-queued' && meta.instantRedeemEnabled !== false) {
    caps.push({
      action: 'withdraw',
      requires: withdrawRequires(provider),
      feeBps: num(meta.withdrawFeeBps) ?? row.exit.feeBps,
    })
  }

  caps.push({
    action: 'request-withdraw',
    async: true,
    requires: requestRequires(provider),
  })
  // GMX has no claim leg: a keeper executes the withdrawal ticket a few
  // seconds after submission and pays out unprompted. Publishing `claim`
  // here produced a button whose transaction cannot exist.
  if (provider !== 'gmx') {
    caps.push({
      action: 'claim',
      async: false,
      requires: claimRequires(row, provider),
    })
  }

  if (supportsCancel(row, provider)) {
    caps.push({
      action: 'cancel',
      requires: savingsVerbRequiresFor(row, provider, 'cancel'),
    })
  }

  return caps
}

/**
 * Does a trade here settle at a price discovered on a BOOK?
 *
 * Only then is a bound meaningful. The distinction inside `priceRisk` matters:
 *
 *  - `none` — a share converts at a rate the contract computes. Nothing to bound.
 *  - `haircut-formula` — a discount that is DETERMINISTIC. The amount is
 *    knowable before sending, so a slippage parameter would be theatre.
 *  - `market-price` / `may-be-impossible` — sold into a book whose depth moves.
 *    Sending no bound accepts any fill.
 *
 * Derived from the trait, so a venue that gains an AMM redemption starts asking
 * for a bound without a change here.
 *
 * NOTE this is deliberately NOT the same question as
 * {@link SWAP_ROUTED_PROVIDERS}, which says there is no mint/redeem leg AT ALL.
 * A venue can be swap-routed (Pendle) or have a mint leg that nonetheless
 * settles on a curve. `swapRoutedProvidersArePriceConsistent` pins that every
 * swap-routed provider is also book-priced, so the two cannot drift apart.
 */
function isBookPriced(provider: string): boolean {
  const risk = vaultTraits(provider)?.priceRisk
  return risk === 'market-price' || risk === 'may-be-impossible'
}

/**
 * Every swap-routed provider must also be book-priced.
 *
 * The two facts are independent in principle but must agree in practice: a
 * provider routed through a swap whose traits claim par pricing would emit a
 * `via: 'swap'` capability while the rest of the surface treated it as a
 * redeem-at-par vault. Exported so a test can assert it rather than leaving it
 * as a comment nobody runs.
 */
export function swapRoutedProvidersArePriceConsistent(): string[] {
  return [...SWAP_ROUTED_PROVIDERS].filter((p) => !isBookPriced(p))
}

/**
 * Extra params an action needs beyond the universal set.
 *
 * Every entry is derived from something the row or its traits actually
 * publishes — never from a hardcoded per-token table. An action that silently
 * omits one of these does not fail politely: it builds a transaction that
 * reverts, or worse, one that fills at any price.
 */
/**
 * The assets an LST mint accepts, with the options each path needs.
 *
 * Read from `calldata-sdk`'s own `LST_INPUT_CAPABILITIES` — the map that sits
 * next to the builders and is drift-guarded by their tests. Re-deriving it here
 * from provider names would be a second source of truth for something the
 * builders already state exactly.
 *
 * Only LSTs publish this today; every other provider takes its underlying and
 * returns `undefined`.
 */
function depositInputs(
  row: EarnMarket,
  provider: string,
): EarnActionInput[] | undefined {
  // Upshift's entry names the asset it pulls, so a vault accepting USDC, USDT
  // and USDS has three real entry paths. They are published the same way an
  // LST's mint paths are, from the listing's own accepted-asset list — absent
  // it, the row degrades to "underlying only", which is the first of them.
  if (provider === 'upshift') {
    const assets = (row.providerMeta ?? {}).depositAssets
    if (!Array.isArray(assets) || assets.length < 2) return undefined
    return assets
      .filter((a: unknown): a is string => typeof a === 'string')
      .map((asset) => ({ asset, mode: 'direct' }))
  }
  if (provider !== 'lst') return undefined
  const share = row.shareToken?.address ?? row.ref
  if (!share) return undefined
  try {
    const accepted = getLstAcceptedInputs(row.chainId, share as `0x${string}`)
    if (!accepted?.length) return undefined
    return accepted.map((a) => ({
      asset: String(a.asset),
      symbol: a.symbol,
      mode: a.mode,
      needs: a.needs,
      optional: a.optional,
    }))
  } catch {
    // A missing registry entry means "underlying only", not a broken row.
    return undefined
  }
}

/**
 * Needs that are a PRICE BOUND rather than a routing choice or a realised
 * amount.
 *
 * `minMETHAmount`, `minRSETHAmountExpected`, `minUsddOut` bound a fill;
 * `stEthAmount` / `eEthAmount` carry the amount the first leg actually produced
 * (deterministic — Lido's submit and wrap have no slippage), and `depositPool`
 * is an address. Only the first class deserves a slippage control.
 */
export function isBoundNeed(need: string): boolean {
  return /^min/i.test(need)
}

function depositRequires(
  provider: string,
  meta: Record<string, any>,
  inputs?: EarnActionInput[],
): string[] | undefined {
  const requires: string[] = []
  // A registry lookup the worker attaches to LST rows: present means the
  // deposit needs a validator pick, absent means pooled.
  if (meta.delegation) requires.push('validator')
  // GMX deposits are keeper tickets funded with a native execution fee (the
  // current minimum travels with the listing as `gmxExecutionFees`), and the
  // builder needs the market's two legs — a GM market is not a share token
  // over one asset. GLV rows additionally need `kind=glv` + `market`; those
  // are documented on the route rather than published here, because the
  // listing does not distinguish GM from GLV rows today.
  if (provider === 'gmx')
    requires.push('executionFee', 'longToken', 'shortToken')
  // Buying into a book-priced position without a bound accepts ANY fill.
  if (isBookPriced(provider)) requires.push('slippage')
  // Needs shared by EVERY accepted input belong on the action; anything
  // path-specific stays on its input, where the UI can ask for it only once
  // that path is chosen.
  if (inputs?.length) {
    const common = inputs[0].needs?.filter((n) =>
      inputs.every((i) => i.needs?.includes(n)),
    )
    for (const n of common ?? []) if (!requires.includes(n)) requires.push(n)
  }
  return requires.length ? requires : undefined
}

/**
 * Exits need the same treatment as entries.
 *
 * A market-priced position is sold back into the same book it was bought
 * from, so the bound is required in BOTH directions — a redeem-at-par vault
 * needs none, a PT needs one every time.
 */
function withdrawRequires(provider: string): string[] | undefined {
  const requires: string[] = []
  if (isBookPriced(provider)) requires.push('slippage')
  if (provider === 'gmx') requires.push('executionFee')
  return requires.length ? requires : undefined
}

function requestRequires(provider: string): string[] | undefined {
  const requires: string[] = []
  if (provider === 'gmx') requires.push('executionFee')
  // A queued exit that settles against a book still needs a floor — depth can
  // move between request and settlement.
  if (isBookPriced(provider)) requires.push('slippage')
  return requires.length ? requires : undefined
}

function claimRequires(
  row: EarnMarket,
  provider: string,
): string[] | undefined {
  // NOTE: incomplete by construction. Apyx's claim needs the ERC-721 unlock
  // receipt's `tokenId`, and Yield Basis has no claim leg at all — but neither
  // is derivable from the listing today, because the row does not publish
  // which savings PROTOCOL it is (only its withdrawal MODE). Phase 0's
  // term-sheet adapter is what supplies this properly; until then a client
  // hitting one of those falls back to the protocol-specific route, exactly as
  // it does now. Do not guess from `withdrawalMode` — several protocols share
  // `request-based` with completely different claim signatures.
  //
  // Upshift is the exception the note allows for: the provider IS published on
  // the row, and its claim is keyed by the DATE the request became claimable
  // (`claim(year, month, day, receiver)`) — a value that exists only in the
  // `claimableEpoch` the request transaction returned. A client that does not
  // keep it has nothing to reconstruct the claim from.
  // Only the split generation — the older Upshift vaults are 4626 share
  // tokens whose exit this module does not describe, so guessing a param for
  // them would be exactly the wrong-signature guess the note warns about.
  const share = row.shareToken?.address ?? row.ref
  if (provider === 'upshift' && share && isSplitUpshiftRow(row, share))
    return ['claimableEpoch']
  return undefined
}

/**
 * The caller-supplied identifier a savings verb needs, read from the builder.
 *
 * Closes the item EARN_ENDPOINT_PLAN.md logged as open before phase 2 —
 * "`claimRequires()` returns `undefined` for Apyx's `tokenId` … because the
 * listing publishes a withdrawal MODE but not the savings PROTOCOL". The
 * listing does not have to: the registry knows the protocol from
 * `(chainId, shareToken)` and the builder states what it wants. Deriving it
 * beats a table for the usual reason — a table is a second copy of a fact the
 * builder already owns.
 *
 * GMX and the LST queues are not savings-registry vaults; their own
 * `requires` helpers cover them.
 */
function savingsVerbRequiresFor(
  row: EarnMarket,
  provider: string,
  verb: 'request-withdraw' | 'claim' | 'cancel',
): string[] | undefined {
  if (provider === 'gmx' || provider === 'lst') return undefined
  const share = row.shareToken?.address ?? row.ref
  if (!share || !row.chainId) return undefined
  try {
    return savingsVerbRequires(
      String(row.chainId),
      share as `0x${string}`,
      verb,
    )
  } catch {
    return undefined
  }
}

/**
 * Can an open request be unwound before it settles?
 *
 * **Asked of the BUILDER, not of the row's exit mode.** Those are different
 * facts and the difference shipped: this used to answer "yes" whenever the row
 * named a `withdrawQueue`, which every Strata market does (its per-market
 * UnstakeCooldown contract) — so twelve rows published a `cancel` that answers
 * `buildSavingsCancelWithdrawal does not handle kind 'strata'`, on a protocol
 * whose own integration note reads "no cancel — shares burn at request".
 * `savingsSupportsVerb` resolves the vault's KIND through the same registry
 * the route reads and asks the builder itself. See
 * EARN_COMPLETENESS_AUDIT.md F9.
 *
 * GMX keeps its hardcoded `true`: its tickets are cancellable through its own
 * route, which the savings dispatcher knows nothing about.
 */
function supportsCancel(row: EarnMarket, provider: string): boolean {
  if (provider === 'gmx') return true
  const share = row.shareToken?.address ?? row.ref
  if (!share || !row.chainId) return false
  try {
    return savingsSupportsVerb(
      String(row.chainId),
      share as `0x${string}`,
      'cancel',
    )
  } catch {
    // Unknown shape is not a capability. Under-advertising costs a button;
    // over-advertising ships one that 400s.
    return false
  }
}
